import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AssignmentCoverImage, RoutineAssignmentItem } from '../../types/assignmentPointsNote';
import type { ItemCard } from '../../types/pointItemNote';
import { calculateRoutineDates } from '../../utils/assignmentPointsNoteProgress';
import {
  pruneRoutineDateStatuses,
  routineEditWouldDeleteRecords,
} from '../../utils/assignmentEditCleanup';
import AssignmentCoverPickField from './AssignmentCoverPickField';
import AssignmentMemberPickField from './AssignmentMemberPickField';
import AssignmentRecordDeleteConfirm from './AssignmentRecordDeleteConfirm';
import AssignmentRewardItemPickModal from './AssignmentRewardItemPickModal';
import type { FlatMember } from './GeneralAssignmentSettingsModal';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import {
  copyTitle,
  CreateDateField,
  CreateRewardCoinField,
  IconMinus,
  IconPlus,
  IconReset,
  newId,
  todayIso,
} from './assignmentCreateModalShared';

const DAY_OPTIONS = [
  { v: 0, label: '일' },
  { v: 1, label: '월' },
  { v: 2, label: '화' },
  { v: 3, label: '수' },
  { v: 4, label: '목' },
  { v: 5, label: '금' },
  { v: 6, label: '토' },
];

const ROUTINE_CATEGORY_COLOR = '#4f46e5';

type Props = {
  open: boolean;
  mode: 'new' | 'edit' | 'copy';
  initial?: RoutineAssignmentItem | null;
  members: FlatMember[];
  defaultGroupId?: string | null;
  defaultPoints: number;
  /** 포인트·아이템 노트의 과제보상 아이템 목록 */
  assignmentRewardItems?: ItemCard[];
  coverImages: AssignmentCoverImage[];
  onCoverImagesChange: (images: AssignmentCoverImage[]) => void;
  onClose: () => void;
  onSave: (item: RoutineAssignmentItem) => void;
};

const RoutineAssignmentSettingsModal: React.FC<Props> = ({
  open,
  mode,
  initial,
  members,
  defaultGroupId = null,
  defaultPoints,
  assignmentRewardItems = [],
  coverImages,
  onCoverImagesChange,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(todayIso());
  const [basePointsDisplay, setBasePointsDisplay] = useState(String(defaultPoints));
  const [notes, setNotes] = useState('');
  const [coverImageId, setCoverImageId] = useState<string | undefined>();
  const [routineDays, setRoutineDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState('0');
  const [rewardItemIds, setRewardItemIds] = useState<string[]>([]);
  const [rewardCompletionRate, setRewardCompletionRate] = useState(100);
  const [itemPickOpen, setItemPickOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<RoutineAssignmentItem | null>(null);

  const getBasePointsNum = () =>
    basePointsDisplay === '' ? 0 : Number(basePointsDisplay) || 0;
  const getRewardCoinsNum = () =>
    rewardCoinsDisplay === '' ? 0 : Number(rewardCoinsDisplay) || 0;

  useEffect(() => {
    if (!open) return;
    setItemPickOpen(false);
    setPendingSave(null);
    if ((mode === 'edit' || mode === 'copy') && initial) {
      setTitle(mode === 'copy' ? copyTitle(initial.title) : initial.title);
      setStartDate(initial.startDate || todayIso());
      setDueDate(initial.dueDate || todayIso());
      setBasePointsDisplay(String(initial.basePoints ?? defaultPoints));
      setNotes(initial.notes ?? '');
      setCoverImageId(initial.coverImageId);
      setRoutineDays(initial.routineDays.length ? [...initial.routineDays] : [1, 2, 3, 4, 5]);
      setSelectedIds(new Set(initial.assignedMemberIds));
      setRewardEnabled(!!initial.rewardEnabled);
      setRewardCoinsDisplay(String(initial.rewardCoins ?? 0));
      setRewardItemIds([...(initial.rewardItemIds ?? [])]);
      setRewardCompletionRate(
        Math.min(100, Math.max(0, initial.rewardCompletionRate ?? 100))
      );
    } else {
      setTitle('');
      setStartDate(todayIso());
      setDueDate(todayIso());
      setBasePointsDisplay(String(defaultPoints));
      setNotes('');
      setCoverImageId(undefined);
      setRoutineDays([1, 2, 3, 4, 5]);
      const defaultMembers = defaultGroupId
        ? members.filter((m) => m.groupId === defaultGroupId)
        : members;
      setSelectedIds(new Set(defaultMembers.map((m) => m.id)));
      setRewardEnabled(false);
      setRewardCoinsDisplay('0');
      setRewardItemIds([]);
      setRewardCompletionRate(100);
    }
  }, [open, mode, initial, defaultPoints, members, defaultGroupId]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const expectedCount = useMemo(
    () => calculateRoutineDates(startDate, dueDate, routineDays).length,
    [startDate, dueDate, routineDays]
  );

  const rewardTargetCount = useMemo(
    () => Math.round((expectedCount * rewardCompletionRate) / 100),
    [expectedCount, rewardCompletionRate]
  );

  const selectedRewardCards = useMemo(() => {
    const map = new Map(assignmentRewardItems.map((c) => [c.itemCardId, c]));
    return rewardItemIds
      .map((id) => map.get(id))
      .filter((c): c is ItemCard => c != null);
  }, [assignmentRewardItems, rewardItemIds]);

  const canSave =
    title.trim().length > 0 &&
    !!startDate &&
    !!dueDate &&
    routineDays.length > 0 &&
    selectedIds.size > 0;

  const heading =
    mode === 'edit' ? '루틴 과제 수정' : mode === 'copy' ? '복제 과제 만들기' : '루틴 과제 생성';

  if (!open) return null;

  const handleSave = () => {
    if (!canSave) return;
    const now = new Date().toISOString();
    const assignedMemberIds = members.filter((m) => selectedIds.has(m.id)).map((m) => m.id);
    const dateStatuses = pruneRoutineDateStatuses(
      mode === 'edit' && initial ? initial.dateStatuses : [],
      startDate,
      dueDate,
      routineDays,
      assignedMemberIds
    );
    const nextItem: RoutineAssignmentItem = {
      id: mode === 'edit' && initial ? initial.id : newId('rtn'),
      kind: 'routine',
      title: title.trim(),
      startDate,
      dueDate,
      routineDays: [...routineDays].sort((a, b) => a - b),
      basePoints: getBasePointsNum(),
      categoryId: undefined,
      notes: notes.trim() || undefined,
      coverImageId,
      rewardEnabled,
      rewardCoins: rewardEnabled ? getRewardCoinsNum() : 0,
      rewardItemIds: rewardEnabled ? rewardItemIds : [],
      rewardCompletionRate: rewardEnabled ? rewardCompletionRate : undefined,
      rewardGrantedStudentIds:
        mode === 'edit' && initial ? initial.rewardGrantedStudentIds : undefined,
      assignedMemberIds,
      dateStatuses,
      createdAt: mode === 'edit' && initial ? initial.createdAt : now,
      updatedAt: now,
    };
    if (mode === 'edit' && initial && routineEditWouldDeleteRecords(initial, nextItem)) {
      setPendingSave(nextItem);
      return;
    }
    onSave(nextItem);
  };

  return createPortal(
    <div className="cp-apn-create-overlay" role="presentation">
      <div
        className="cp-apn-create-overlay-dim"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="cp-apn-create-overlay-scroll"
        role="presentation"
        onClick={onClose}
      >
        <div
          className="cp-apn-create-modal"
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          onClick={(e) => e.stopPropagation()}
        >
        <h2 className="cp-apn-create-title">{heading}</h2>

        <AssignmentCoverPickField
          coverImageId={coverImageId}
          coverImages={coverImages}
          onCoverImageIdChange={setCoverImageId}
          onCoverImagesChange={onCoverImagesChange}
        >
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">과제 이름</label>
            <input
              className="cp-apn-create-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 양치하기"
            />
          </div>
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">과제 설명 (선택사항)</label>
            <textarea
              className="cp-apn-create-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
              rows={3}
            />
          </div>
        </AssignmentCoverPickField>

        <section
          className="cp-apn-create-section cp-apn-create-section--compact"
          data-tutorial-apn-create="reward"
        >
          <div className="cp-apn-create-toggle-row">
            <span className="cp-apn-create-label">루틴 과제 완료 보상</span>
            <button
              type="button"
              className={`cp-apn-create-toggle${rewardEnabled ? ' is-on' : ''}`}
              aria-pressed={rewardEnabled}
              onClick={() => setRewardEnabled((v) => !v)}
            >
              <span className="cp-apn-create-toggle-knob">{rewardEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>
          {rewardEnabled ? (
            <>
              <CreateRewardCoinField
                value={rewardCoinsDisplay}
                onChange={setRewardCoinsDisplay}
                getNum={getRewardCoinsNum}
              />
              <div className="cp-apn-create-field">
                <label className="cp-apn-create-label">
                  획득 카드 설정 (루틴 과제 보상 아이템)
                </label>
                <div className="cp-apn-reward-cards">
                  {selectedRewardCards.map((card) => (
                    <div key={card.itemCardId} className="cp-apn-reward-card">
                      <div className="cp-apn-reward-card-thumb" aria-hidden>
                        {resolveItemCardImageSrc(card.cardImage) ? (
                          <img src={resolveItemCardImageSrc(card.cardImage)!} alt="" />
                        ) : (
                          <span>카드</span>
                        )}
                      </div>
                      <div className="cp-apn-reward-card-name">{card.name}</div>
                      {card.memo ? (
                        <div className="cp-apn-reward-card-memo">{card.memo}</div>
                      ) : null}
                      <button
                        type="button"
                        className="cp-apn-reward-card-remove"
                        aria-label="카드 제거"
                        onClick={() =>
                          setRewardItemIds((prev) =>
                            prev.filter((id) => id !== card.itemCardId)
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cp-apn-reward-card-add"
                    onClick={() => setItemPickOpen(true)}
                  >
                    <span className="cp-apn-reward-card-add-icon">+</span>
                    <span>아이템 카드 추가</span>
                  </button>
                </div>
              </div>
              <div className="cp-apn-create-field">
                <div className="cp-apn-rate-label-wrap">
                  <label className="cp-apn-create-label is-center">과제 완료 도달률 설정</label>
                  <div className="cp-apn-rate-value">
                    {rewardCompletionRate}% ({rewardTargetCount}/{expectedCount}회)
                  </div>
                </div>
                <div
                  className="cp-apn-rate-slider-wrap"
                  style={{ ['--cp-apn-rate' as string]: `${rewardCompletionRate}%` }}
                >
                  <div className="cp-apn-rate-slider-track" aria-hidden />
                  <div className="cp-apn-rate-slider-fill" aria-hidden />
                  <input
                    className="cp-apn-rate-slider"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={rewardCompletionRate}
                    aria-label="과제 완료 도달률"
                    onChange={(e) => setRewardCompletionRate(Number(e.target.value))}
                  />
                </div>
              </div>
            </>
          ) : null}
        </section>

        <section className="cp-apn-create-section" data-tutorial-apn-create="schedule">
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label is-center">반복 요일</label>
            <div className="cp-apn-create-days">
              {DAY_OPTIONS.map((d) => {
                const on = routineDays.includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    className={`cp-apn-create-day${on ? ' is-on' : ''}`}
                    onClick={() =>
                      setRoutineDays((prev) =>
                        on ? prev.filter((x) => x !== d.v) : [...prev, d.v]
                      )
                    }
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <p className="cp-apn-create-days-hint">예상 횟수: {expectedCount}회</p>
          </div>

          {/* 1행: 시작/마감 날짜 */}
          <div className="cp-apn-create-field-row">
            <CreateDateField label="시작 날짜" value={startDate} onChange={setStartDate} />
            <CreateDateField label="마감 날짜" value={dueDate} onChange={setDueDate} />
          </div>

          {/* 2행: 획득 포인트 */}
          <div className="cp-apn-create-field">
            <div className="cp-apn-create-label-row">
              <label className="cp-apn-create-label">획득 포인트</label>
              <div className="cp-apn-create-inc">
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="포인트 초기화"
                  onClick={() => setBasePointsDisplay('0')}
                >
                  <IconReset />
                </button>
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="포인트 빼기"
                  onClick={() =>
                    setBasePointsDisplay(
                      String(Math.max(0, getBasePointsNum() - (defaultPoints || 1)))
                    )
                  }
                >
                  <IconMinus />
                </button>
                <button
                  type="button"
                  className="cp-apn-create-inc-btn"
                  aria-label="포인트 더하기"
                  onClick={() =>
                    setBasePointsDisplay(String(getBasePointsNum() + (defaultPoints || 1)))
                  }
                >
                  <IconPlus />
                </button>
              </div>
            </div>
            <input
              className="cp-apn-create-input"
              type="number"
              min={0}
              value={basePointsDisplay}
              onChange={(e) => setBasePointsDisplay(e.target.value)}
              onBlur={() => {
                if (basePointsDisplay === '') setBasePointsDisplay('0');
              }}
            />
          </div>

          {/* 3행: 카테고리 (루틴 고정) */}
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label is-center">카테고리</label>
            <div className="cp-apn-create-cat-fixed" aria-label="카테고리 루틴">
              <span
                className="cp-apn-create-cat-dot"
                style={{ background: ROUTINE_CATEGORY_COLOR }}
              />
              <span>루틴</span>
            </div>
          </div>
        </section>

        <section className="cp-apn-create-section" data-tutorial-apn-create="members">
          <AssignmentMemberPickField
            members={members}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            initialGroupId={defaultGroupId}
            educodiLayout
          />
        </section>

        <div className="cp-apn-create-buttons">
          <button type="button" className="cp-apn-create-btn-cancel" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-apn-create-btn-save"
            disabled={!canSave}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
        </div>
      </div>

      <AssignmentRecordDeleteConfirm
        open={pendingSave != null}
        kind="routine"
        onCancel={() => setPendingSave(null)}
        onConfirm={() => {
          if (!pendingSave) return;
          onSave(pendingSave);
          setPendingSave(null);
        }}
      />

      <AssignmentRewardItemPickModal
        open={itemPickOpen}
        items={assignmentRewardItems}
        initialSelectedIds={rewardItemIds}
        onClose={() => setItemPickOpen(false)}
        onConfirm={(ids) => {
          setRewardItemIds(ids);
          setItemPickOpen(false);
        }}
      />
    </div>,
    document.body
  );
};

export default RoutineAssignmentSettingsModal;
